import React from 'react';
import PropTypes from 'prop-types';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const NlVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4 id={id || 'cookiebeleid'} data-magellan-target={id || 'cookiebeleid'}>
        Cookiebeleid
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Datum inwerkingtreding: 22 augustus 2023</p>
      <p>
        Dit Cookiebeleid (‘Beleid’) legt uit hoe Plaid Inc. en hun
        dochterondernemingen, waaronder Plaid Financial Ltd., Plaid Consumer
        Reporting Agency, Inc., en Plaid, B.V., (gezamenlijk ‘wij’, ‘onze’, of
        ‘ons’), en vertrouwde derden, cookies gebruiken op onze websites en
        andere online diensten (gezamenlijk de ‘Diensten’), alsmede uw keuzes
        met betrekking tot die cookies. Lees dit Beleid om meer te weten te
        komen over de soorten cookies die we gebruiken, waarom we ze gebruiken
        en hoe u het gebruik van cookies kunt beheren of beperken.
      </p>
      <p>
        Voor meer informatie over onze privacypraktijken raadpleegt u ons{' '}
        <Link href='#privacybeleid-voor-eindgebruikers'>
          <a>Privacybeleid voor eindgebruikers</a>
        </Link>{' '}
        en onze{' '}
        <Link href='#privacyverklaring'>
          <a>Privacyverklaring</a>
        </Link>
        .
      </p>

      <p>
        <em>Spring naar onderdeel:</em>
      </p>
      <p>
        <Link href='#wat-zijn-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Wat zijn cookies?
          </a>
        </Link>
      </p>

      <p>
        <Link href='#hoe-wij-cookies-gebruiken'>
          <a data-smooth-scroll data-offset='80'>
            Hoe we cookies gebruiken
          </a>
        </Link>
      </p>

      <p>
        <Link href='#uw-keuzes-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Uw keuzes
          </a>
        </Link>
      </p>

      <p>
        <Link href='#wijzigingen-in-dit-beleid-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Wijzigingen in dit beleid
          </a>
        </Link>
      </p>

      <p>
        <Link href='#contact-opnemen-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Contact opnemen
          </a>
        </Link>
      </p>

      <h5 id='wat-zijn-cookies'>Wat zijn cookies?</h5>
      <p>
        Cookies zijn kleine gegevensbestanden die op uw browser of apparaat
        worden opgeslagen in de vorm van sessiecookies (die verlopen zodra u uw
        webbrowser sluit) of permanente cookies (die gedurende een bepaalde
        periode op uw browser blijven staan of totdat u ze verwijdert). Ze
        bevatten over het algemeen informatie zoals websitevoorkeuren,
        gebruikersinstellingen en browsegeschiedenis. Deze informatie helpt ons
        u te herkennen wanneer u onze Diensten gebruikt of opnieuw bezoekt.
      </p>
      <p>
        Cookies kunnen worden aangeboden door de entiteit die de website beheert
        die u bezoekt (‘eerstepartijcookies’) of door andere bedrijven (‘cookies
        van derden’). Zo gebruiken we bijvoorbeeld eerstepartijcookies om uw
        instellingen en voorkeuren, zoals taal en locatie, te onthouden. Ook
        integreren we analytische cookies van derden op onze websites, zoals
        Google Analytics, om ons te helpen begrijpen hoe u onze websites
        gebruikt, met als doel deze te verbeteren.
      </p>
      <p>
        Daarnaast gebruiken we ook andere technologieën die op dezelfde manier
        werken als cookies. Zo maken we bijvoorbeeld gebruik van webbakens (ook
        wel pixels genoemd), wat kleine afbeeldingen op een webpagina of in een
        e-mail zijn. Webbakens verzamelen informatie over uw browser of apparaat
        en kunnen cookies instellen. Bovendien maken we gebruik van lokale
        opslag, waardoor gegevens lokaal op uw browser of apparaat kunnen worden
        opgeslagen.
      </p>

      <h5 id='hoe-wij-cookies-gebruiken'>Hoe we cookies gebruiken</h5>
      <p>
        We gebruiken cookies voor verschillende doeleinden, zoals het helpen
        bepalen van de populariteit van bepaalde inhoud, het verbeteren van onze
        Diensten en uw ervaring, en om uw online activiteiten beter te
        begrijpen. De cookies die we gebruiken vallen over het algemeen in een
        of meer van de volgende categorieën:
      </p>
      <div className='eupp-container'>
        <table>
          <thead>
            <tr>
              <th>Categorie van de cookies</th>
              <th>Hoe we deze cookies gebruiken</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Strikt noodzakelijk</td>
              <td>
                Deze cookies zijn strikt noodzakelijk voor de goede werking van
                onze Diensten. Sommige van onze Diensten zijn afhankelijk van
                deze cookies en werken mogelijk niet goed of helemaal niet als
                ze zijn uitgeschakeld.
              </td>
            </tr>
            <tr>
              <td>Functioneel</td>
              <td>
                Met deze cookies kan de website verbeterde functionaliteit en
                personalisatie bieden. Ze kunnen worden ingesteld door ons of
                door externe leveranciers van wie we diensten aan onze pagina's
                hebben toegevoegd. Als u deze cookies niet toestaat, is het
                mogelijk dat sommige of al deze diensten niet goed functioneren.
              </td>
            </tr>
            <tr>
              <td>Prestaties</td>
              <td>
                We gebruiken deze cookies om beter te begrijpen hoe u omgaat met
                onze Diensten, zodat we deze kunnen verbeteren. <br />
                <br />
                We kunnen deze cookies bijvoorbeeld gebruiken om te bepalen of u
                interactie met bepaalde inhoud of functies heeft gehad. We
                kunnen deze cookies ook gebruiken om meer te weten te komen over
                welke functies het populairst zijn bij onze gebruikers en waar
                we eventueel verbeteringen moeten aanbrengen.
              </td>
            </tr>
            <tr>
              <td>Targeting/reclame</td>
              <td>
                Wij en onze vertrouwde advertentiepartners gebruiken deze
                cookies om advertenties te tonen, om advertenties voor bezoekers
                van onze websites relevanter te maken en om de efficiëntie van
                advertentiecampagnes bij te houden, zowel van onze Diensten als
                op andere websites.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='uw-keuzes-cookies'>Uw keuzes</h5>
      <p>
        U heeft het recht om te kiezen of u cookies wel of niet accepteert. Maar
        aangezien cookies echter een belangrijk onderdeel kunnen zijn van de
        werking van onze Diensten, kan het verwijderen of weigeren van cookies
        de beschikbaarheid en functionaliteit van onze Diensten beïnvloeden.
      </p>
      <p>
        Hieronder vindt u enkele opties om het gebruik van cookies voor onze
        Diensten te controleren of te beperken:
      </p>
      <ul>
        <li>
          <p>
            U kunt binnen de configuratie-instellingen van uw browser de
            instellingen om browsercookies te verwijderen of te weigeren meestal
            handmatig beheren. Volg de instructies van uw browser om deze
            instellingen te beheren.
          </p>
        </li>
        <li>
          <p>
            Om te voorkomen dat uw gegevens door Google Analytics worden
            gebruikt, kunt u de{' '}
            <Link href='https://tools.google.com/dlpage/gaoptout?hl=nl'>
              <a>opt-out browser add-on van Google</a>
            </Link>{' '}
            installeren.
          </p>
        </li>
        <li>
          <p>
            Voor informatie over hoe onze reclamepartners u de mogelijkheid
            kunnen bieden om af te zien van het ontvangen van advertenties op
            basis van uw browsegeschiedenis, gaat u naar{' '}
            <Link href='http://optout.aboutads.info/'>
              <a>http://optout.aboutads.info/</a>
            </Link>
            . Bovendien kunnen Europese gebruikers de{' '}
            <Link href='http://www.youronlinechoices.eu/'>
              <a>European Interactive Digital Advertising Alliance</a>
            </Link>{' '}
            bezoeken en Canadese gebruikers de{' '}
            <Link href='https://youradchoices.ca/'>
              <a>Digital Advertising Alliance of Canada</a>
            </Link>{' '}
            om meer informatie te krijgen over hoe zij zich kunnen afmelden
            teneinde dit soort advertenties niet meer te ontvangen.
          </p>
        </li>
      </ul>

      <h5 id='wijzigingen-in-dit-beleid-cookies'>Wijzigingen in dit Beleid</h5>
      <p>
        We kunnen de cookies (of vergelijkbare technologieën) die we gebruiken
        van tijd tot tijd bijwerken of wijzigen. De meest recente versie van dit
        Beleid wordt op onze website{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>{' '}
        geplaatst en als we wijzigingen aanbrengen zullen we de ingangsdatum
        bovenaan het Beleid bijwerken. We raden u aan om regelmatig te
        controleren of er wijzigingen zijn aangebracht.
      </p>

      <h5 id='contact-opnemen-cookies'>Contact opnemen</h5>
      <p>
        Als u vragen heeft over ons gebruik van cookies of dit Beleid, kunt u
        contact met ons opnemen via{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>.
        </Link>
      </p>

      <hr className='section-divider' />
    </>
  );
};

NlVersion.propTypes = propTypes;
export default NlVersion;
